/**
 * PhotoLightbox — full-resolution photo overlay (2026-05-06).
 *
 * Phase 13 Plan 02 — Backdrop migrated from bg-black/90 (literal) to
 * bg-on-surface/90 (Phase 8 brand token, resolves to #1B1C18 = rgb(27, 28, 24)).
 * Photos retain color fidelity against near-black; backdrop now consumes the
 * unified brand-token surface per CONTEXT.md D-07. (PHOTOSTRIP-6.2)
 *
 * Opens when the operator clicks a landed photo thumbnail in PhotoStrip.
 * Renders the full-resolution stripped JPEG via the PM-auth-gated endpoint
 * `/api/admin/photos/{id}/binary` (server/app/photos/binary_router.py).
 * Thumbnails (320px) are too small to triage condition; this is the
 * "actually look at the damage" view.
 *
 * Close affordances:
 *  - Click the X button in the top-end corner.
 *  - Press Escape.
 *  - Click the dark backdrop (anywhere outside the image and the close
 *    button).
 *
 * Photos do NOT mirror in RTL per ~/.claude/CLAUDE.md (the close button
 * uses logical `end` positioning so it lands top-right in LTR and top-left
 * in RTL, but the photo itself is content-canonical).
 *
 * No focus trap or pointer-event lock for v1 — this is a PM-side review
 * tool, not a critical-path workflow modal. Add focus management here if
 * the v2 audit flags it.
 */
import { useEffect } from "react";
import { useIntl } from "react-intl";
import { X } from "lucide-react";

interface PhotoLightboxProps {
  photoId: number;
  onClose: () => void;
}

export function PhotoLightbox({ photoId, onClose }: PhotoLightboxProps) {
  const intl = useIntl();

  // Esc-to-close. Captured at window so it fires even when focus is on the
  // image (which is the default since the close button is rendered on top
  // but not auto-focused).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="photo-lightbox"
      onClick={(e) => {
        // Only close on backdrop clicks — image and close button stop
        // propagation so the operator can interact with them without
        // dismissing.
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/90 p-4"
    >
      <img
        src={`/api/admin/photos/${photoId}/binary`}
        alt={intl.formatMessage({ id: "HIST_PHOTO_ALT" }, { id: photoId })}
        className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
        data-testid="photo-lightbox-image"
        onClick={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        onClick={onClose}
        aria-label={intl.formatMessage({ id: "LABEL_DISMISS" })}
        data-testid="photo-lightbox-close"
        className="absolute top-4 end-4 rounded-full bg-surface-raised/95 hover:bg-surface-raised p-2 text-on-surface shadow-lg focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200"
      >
        <X size={20} aria-hidden="true" />
      </button>
    </div>
  );
}
